import {InMemoryCache} from 'apollo-cache-inmemory'
import {ApolloClient} from 'apollo-client'
import {ApolloLink, from} from 'apollo-link'
import {onError} from 'apollo-link-error'
import {setContext} from 'apollo-link-context'
import React, {Component} from 'react'
import {ApolloProvider} from 'react-apollo'
import {AUTH_TOKEN} from '../constants';
import {resolvers, typeDefs} from './localSchema'
import {createUploadLink} from 'apollo-upload-client';

const protocol = window.location.protocol.concat('//');
const hostname = window.location.hostname;
const host = `${protocol}${hostname}`;
export const graphQlUrl = `${host}/graphql`;

const httpLink = createUploadLink({
  uri: graphQlUrl
});

export const getHeaders = ( headers: any = {}) => {
  const token = localStorage.getItem(AUTH_TOKEN);

  if(token) {
    headers = {
      ...headers,
      authorization: `Bearer ${token}`,
    }
  }

  return {...headers}
};

const authLink = setContext((_, { headers }) => {
  return {
    headers: getHeaders(headers)
  }
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  const publicAuthPaths = ['/login', '/password/forgot', '/password/reset', '/booking'];
  const isOnPublicAuthPage = publicAuthPaths.some(path => window.location.pathname.startsWith(path));

  // Handle GraphQL errors (auth failures return as GraphQL errors with HTTP 200)
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Check for authentication failures
      if (err.message === 'Unauthenticated.' ||
          (err.extensions && err.extensions.guards && err.extensions.guards.includes('api'))) {
        // Only redirect if not already on a public auth page
        if (!isOnPublicAuthPage) {
          clearCache();
        } else {
          // On public auth pages, just clear the invalid token without redirecting
          localStorage.removeItem(AUTH_TOKEN);
        }
        return;
      }
    }
  }

  // Handle network-level errors (e.g., 401 HTTP status from OAuth endpoints)
  if (networkError) {
    const statusCode = (networkError as any).statusCode;
    if (statusCode === 401) {
      if (!isOnPublicAuthPage) {
        clearCache();
      } else {
        localStorage.removeItem(AUTH_TOKEN);
      }
      return;
    }
  }
});

const cache = new InMemoryCache();

const client = new ApolloClient({
  cache,
  link: from([errorLink, authLink.concat(httpLink)]),
  typeDefs,
  resolvers,
});

class GraphQlProvider extends Component<{ children?: any; }> {
  public render() {
    return (
      <ApolloProvider client={client}>
        {this.props.children}
      </ApolloProvider>
    )
  }
}

//set default graphql data
cache.writeData({
  data: {},
});

export const getHost = () => host;

export const writeLocalAuthToken = (token: string) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('On local dev server, setting the access token in Local Storage');
  // }
  localStorage.setItem(AUTH_TOKEN, token);
};

export const clearCache = (redirect:boolean=true) => {
  localStorage.removeItem(AUTH_TOKEN);
  client.clearStore()
    .then(() => {
    //Once the store is clear, redirect to login without using the router to clear the in memory cache
    if(redirect) window.location.href = '/login';
  });
};

export const updateCacheByType = (typeNameInCache: string[]|string) => {
    return (cacheRef: any) => {
        Object.keys(cacheRef.data.data).forEach((key) => {
            if (Array.isArray(typeNameInCache)) {
                typeNameInCache.forEach((typeName) => {
                    if (key.match(`^${typeName}`)) {
                        cacheRef.data.delete(key);
                    }
                });
            }else {
                if (key.match(`^${typeNameInCache}`)) {
                    cacheRef.data.delete(key);
                }
            }
        });

        client.reFetchObservableQueries();
    };
};

export default GraphQlProvider;
